<script setup lang="ts">
import type { HTMLAttributes, StyleValue } from 'vue';
import { computed, useAttrs } from 'vue';
import { RouterLink, type RouterLinkProps } from 'vue-router';

export interface Props {
  to?: RouterLinkProps['to'];
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  class?: HTMLAttributes['class'];
  disabled?: boolean;
  underline?: boolean;
  type?: 'error' | 'success' | 'warning' | 'info' | 'default';
}

const props = withDefaults(defineProps<Props>(), {
  target: '_self',
  disabled: false,
  type: 'default',
  underline: false,
});

defineOptions({ inheritAttrs: false });

const attr = useAttrs();

const emits = defineEmits(['click']);

// 判断是否为外部链接
const isExternal = computed(() => {
  if (props.href) {
    return props.href.startsWith('http://') || props.href.startsWith('https://');
  }
  return false;
});

// 判断是否为内部路由
const isRouterLink = computed(() => {
  return !isExternal.value || props.to;
});

// 计算链接地址
const linkAddress = computed(() => {
  return props.href || props.to || '#';
});

// 是否是链接
const isLink = computed(() => {
  return props.href || props.to;
});

// 处理点击事件
const handleClick = (event: Event) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  emits('click', event);
};

const displayStyle = computed(() => attr.style as StyleValue);

const displayClass = computed(() => {
  return [
    'link',
    `type-${props.type}`,
    { 'link-underline': props.underline, 'link-disabled': props.disabled },
    props.class,
  ];
});
</script>

<template>
  <span v-if="!isLink" :class="displayClass" :style="displayStyle" @click="handleClick">
    <slot></slot>
  </span>
  <RouterLink
    v-else-if="isRouterLink"
    :to="linkAddress"
    :class="displayClass"
    :style="displayStyle"
    @click="handleClick"
  >
    <slot></slot>
  </RouterLink>
  <a
    v-else
    :href="href"
    :target="target"
    :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
    :class="displayClass"
    :style="displayStyle"
    @click="handleClick"
  >
    <slot></slot>
  </a>
</template>

<style scoped lang="scss">
@use '../../styles/mixins.scss' as *;

/* 链接基础样式 */
a,
span {
  display: inline;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

/* 禁用状态 */
.link-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* 下划线样式 */
.link-underline:hover {
  text-decoration: underline;
}

/* 焦点样式 */
a:focus-visible,
span:focus-visible {
  outline: 2px solid var(--x-purple-3);
  outline-offset: 2px;
}

/* 主题样式 */
.type-default {
  color: var(--x-text-1);
  &:hover:not(.link-disabled) {
    color: var(--x-text-3);
  }
}

.type-error {
  color: var(--x-error-5);
  &:hover:not(.link-disabled) {
    color: var(--x-error-6);
  }
}

.type-success {
  color: var(--x-success-5);
  &:hover:not(.link-disabled) {
    color: var(--x-success-6);
  }
}

.type-warning {
  color: var(--x-warning-5);
  &:hover:not(.link-disabled) {
    color: var(--x-warning-6);
  }
}

.type-info {
  color: var(--x-purple-5);
  font-weight: 400;
  &:hover:not(.link-disabled) {
    color: var(--x-purple-6);
  }
}
</style>
